// React imports
import { useEffect, useState } from "react";

// Material UI imports
import {
  MenuItem,
  Box,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Button,
  Select,
  InputLabel,
  Slider,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

// ERP Common imports
import Checkbox from "@/components/checkbox/checkbox";
import Radio from "@/components/radio/radio";
import TextField from "@/components/text-field/text-field";
import Typography from "@/components/typography/typography";

// Local imports
import FieldSelect from "./field-select";
import "../custom-form.scss";

interface Option {
  label: string;
  value: string;
  is_checked: boolean;
  [key: string]: string | boolean; // Index signature to allow any string property
}

export function CheckboxEditForm(props: any) {
  const [isAddSelectOption, setAddSelectOption] = useState(false);
  const { handlePropertyValueUpdate, handleSelectOptionValueUpdate , handleColumnSelect ,  handleWidthChange  } = props;
  const columnPerRowOptions = [1, 2, 3, 4, 5, 6];
  const [selectOptions, setSelectOptions] = useState(
    props.element.options || [],
  );
  const [elementJSON, setElementJSON] = useState(props.element);

  const allowLabelPositionModification =
    elementJSON.field_type !== "radioButton";

  const [newOptions, setNewOptions] = useState<Option>({
    label: "",
    value: "",
    is_checked: false,
  });



  const handleAddNewOption = (e: any) => {
    const label: string = e.target.name;
    const value: string = e.target.value;
    const option: Option = { ...newOptions };
    option[label] = value;
    setNewOptions(option);
  };

  const handleFieldUpdate = (e: any) => {
    elementJSON.field_type = e.target.value;
    handlePropertyValueUpdate(null, null, null, e.target.value);
  };

  const handleSelectUpdate = (e: any) => {
    elementJSON.default_value = e.target.value;
    handlePropertyValueUpdate(null, null, e.target.value);
  };

  useEffect(() => {
    setElementJSON(props.element);
    setSelectOptions(props.element.options || []);
  }, [props.element]);

  useEffect(() => {
    setNewOptions({ label: "", value: "", is_checked: false });
    setAddSelectOption(false);
    handleSelectOptionValueUpdate(selectOptions);
    // eslint-disable-next-line
  }, [selectOptions]);

  return (
    <Box className="formSection--FieldContainer" key={elementJSON.id}>
      <div key={elementJSON.id}>
        <FieldSelect
          key={elementJSON.id}
          variant="outlined"
          onChange={handleFieldUpdate}
          defaultValue={elementJSON.field_type}
          label="Field Type"
          disabled={elementJSON.type === "system" || !elementJSON.is_new}
        />
      </div>
      <div key={elementJSON.id}>
        <TextField
          fullWidth
          label="Label"
          is_required
          key={elementJSON.id}
          name="label"
          type="text"
          onChange={handlePropertyValueUpdate}
          defaultValue={elementJSON.label}
        />
      </div>

      <div key={elementJSON.id}>
        <Select
          variant="outlined"
          key={elementJSON.id}
          fullWidth
          placeholder="Select Default"
          size="small"
          name="options"
          onChange={() => handleSelectUpdate}
          defaultValue={elementJSON.default_value}
          label="Default"
          disabled={!selectOptions.length}
        >
          {selectOptions?.map((option: any, index: number) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>
      <FormControl>
        <Select
          key={elementJSON.id}
          fullWidth
          placeholder="Select Column Per Row"
          size="small"
          onChange={handleColumnSelect} // need to change
          // defaultValue= {} // need to change
          label="Select Column Per Row"
          name="option"
          variant="outlined"
        >
          {columnPerRowOptions?.map((option: any, index: number) => (
            <MenuItem key={index} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box>
        <Typography sx={{ color: 'black' }}>Adjust Width:</Typography>
        <Slider
          value = {elementJSON?.custom_width}
          name = "custom_width"
          min={1}
          max={12}
          step={1}
          onChange={(_, newValue) => handleWidthChange(newValue)}
          aria-labelledby="width-slider"
          sx = {{color : "#4AC08C"}}
        />
      </Box>
    

      <div className="formSection--SelectOption">
        <Typography type="s5" weight={"medium"} color={"theme.secondary.800"}>
          Options
        </Typography>
        {selectOptions.map((option: any, index: number) => (
          <div className="formSection--MultiOptions">
            <TextField
              fullWidth
              key={index}
              value={`${option.label} (${option.value})`}
              InputProps={{
                readOnly: true,
              }}
            />
            <CloseIcon
              style={{ fontSize: "1rem", color: "#656669" }}
              onClick={() => {
                setSelectOptions([
                  ...selectOptions.filter(
                    (_: any, filterIndex: number) => filterIndex !== index,
                  ),
                ]);
              }}
            />
          </div>
        ))}
      </div>
      {isAddSelectOption ? (
        <div className="formSection--SelectOption">
          <div>
            <TextField
              fullWidth
              label="Label"
              is_required
              name="label"
              type="text"
              placeholder="Label"
              defaultValue={newOptions.label}
              onChange={handleAddNewOption}
            />
          </div>
          <div>
            <TextField
              fullWidth
              label="Value"
              placeholder="Value"
              is_required
              name="value"
              type="text"
              defaultValue={newOptions.value}
              onChange={handleAddNewOption}
            />
          </div>
          <Button
            onClick={() =>
              setSelectOptions([...selectOptions, { ...newOptions }])
            }
            disabled={
              !!selectOptions.find(
                (ele: Option) => ele.value === newOptions.value,
              )
            }
            variant="text"
            className="formSection--Button"
            startIcon={<Add />}
          >
            Add
          </Button>
        </div>
      ) : (
        <Button
          onClick={() => setAddSelectOption(true)}
          variant="text"
          className="formSection--Button"
          startIcon={<Add />}
        >
          Add Option
        </Button>
      )}

      <FormControl>
        <Typography type="s4" color="theme.secondary.800">
          Display style
        </Typography>
        <RadioGroup
          key={elementJSON.id}
          row
          aria-labelledby="demo-radio-buttons-group-label"
          name="display"
          onChange={handlePropertyValueUpdate}
          defaultValue={elementJSON.display}
        >
          <FormControlLabel
            value="row"
            control={<Radio size="small" />}
            label="Row"
            slotProps={{ typography: { variant: "body2" } }}
          />
          <FormControlLabel
            value="column"
            control={<Radio size="small" />}
            label="Column"
            slotProps={{ typography: { variant: "body2" } }}
          />
        </RadioGroup>
      </FormControl>

      <div key={elementJSON.id}>
        <TextField
          key={elementJSON.id}
          fullWidth
          label="Title"
          is_required
          name="title"
          type="text"
          onChange={handlePropertyValueUpdate}
          defaultValue={elementJSON.title}
        />
      </div>

      <FormControl disabled={!allowLabelPositionModification}>
        <Typography type="s4" color="theme.secondary.800">
          Title Position
        </Typography>
        <RadioGroup
          key={elementJSON.id}
          row
          aria-labelledby="demo-radio-buttons-group-label"
          name="title_position"
          onChange={handlePropertyValueUpdate}
          defaultValue={allowLabelPositionModification ? "end" : ""}
        >
          <FormControlLabel
            value="start"
            control={<Radio size="small" />}
            label="Start"
            slotProps={{ typography: { variant: "body2" } }}
          />
          <FormControlLabel
            value="end"
            control={<Radio size="small" />}
            label="End"
            slotProps={{ typography: { variant: "body2" } }}
          />
        </RadioGroup>
      </FormControl>

      <FormControlLabel
        key={elementJSON.id}
        name="is_required"
        control={<Checkbox />}
        onChange={handlePropertyValueUpdate}
        checked={elementJSON.is_required}
        label="This field is required"
      />
      <FormControlLabel
        key={elementJSON.id}
        name="is_unique"
        control={<Checkbox />}
        onChange={handlePropertyValueUpdate}
        checked={elementJSON.is_unique}
        label="Is Unique"
      />

      <FormControlLabel
        key={elementJSON.id}
        name="is_fullwidth"
        control={<Checkbox />}
        onChange={handlePropertyValueUpdate} // need to change this
        checked={elementJSON?.is_fullwidth}// need to change 
        label="Full Width "
      />
    </Box>
  );
}

export default CheckboxEditForm;
